import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDateFnsLocale } from '../../hooks/useDateFnsLocale';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { Lock, Globe, Users, User, AlertTriangle, Eye, Calendar, Crown, UserCheck, Sparkles } from 'lucide-react';
import { Button, Input, Modal, showToast } from '../common';
import { useTaskStore } from '../../stores/taskStore';
import { useProjectStore } from '../../stores/projectStore';
import { useUserStore } from '../../stores/userStore';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { aiChatApi } from '../../services/aiChatApi';
import { useRelativeTime } from '../../hooks';
import { getRelativeTime } from '../../utils/relativeTime';
import { RecurringRuleForm } from './RecurringRuleForm';
import { TaskAttachmentSection, type PendingAttachment } from './TaskAttachmentSection';
import type { TaskPriority, CreateTaskRequest, UpdateTaskRequest, RecurringRule, ProjectRole } from '../../types';

interface TaskFormData {
  title: string;
  description: string;
  priority: TaskPriority;
  dueDate: string;
  remindAt: string;
  projectId: string;
  assigneeId: string;
  labels: string;
  isPrivate: boolean;
  isGroupTask: boolean;
}

// FIX: Group C - Component for viewing completed tasks in read-only mode
const TaskViewMode: React.FC<{ task: any; currentUserId: string | undefined; onClose: () => void }> = ({ task, currentUserId, onClose }) => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateFnsLocale();
  // Relative time for task creation
  const { relativeTime: createdAgo } = useRelativeTime(task.createdAt);

  // Determine task relationship to current user for visual badges
  const isCreatedByMe = task.creatorId === currentUserId;
  const isAssignedToMe = task.assigneeId === currentUserId;
  const showCreatedBadge = isCreatedByMe && !isAssignedToMe;
  const showAssignedBadge = isAssignedToMe && !isCreatedByMe;
  const showBothBadges = isCreatedByMe && isAssignedToMe;
  
  return (
    <div className="space-y-4">
      {/* Ownership badges - prominent display */}
      {(showCreatedBadge || showAssignedBadge || showBothBadges) && (
        <div className="flex flex-wrap gap-2 mb-2">
          {showCreatedBadge && (
            <span 
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
              title={t("tasks.card.createdByYou")}
            >
              <Crown className="w-3.5 h-3.5" />
              {t("tasks.card.createdByMe")}
            </span>
          )}
          {showAssignedBadge && (
            <span 
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"
              title={t("tasks.card.assignedToYou")}
            >
              <UserCheck className="w-3.5 h-3.5" />
              {t("tasks.card.assignedToMe")}
            </span>
          )}
          {showBothBadges && (
            <span 
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400"
              title={t("tasks.card.createdAndAssignedToYou")}
            >
              <Crown className="w-3.5 h-3.5" />
              <UserCheck className="w-3.5 h-3.5" />
              {t("tasks.card.createdAndAssigned")}
            </span>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("tasks.form.titleLabel")}</label>
        <p className="text-gray-900 dark:text-gray-100 font-medium">{task.title}</p>
      </div>
      
      {task.description && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("tasks.form.descriptionLabel")}</label>
          <p className="text-gray-700 dark:text-gray-300">{task.description}</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("tasks.form.priorityLabel")}</label>
          <p className={`inline-flex items-center px-2 py-1 rounded-full text-sm ${
            task.priority === 'URGENT' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
            task.priority === 'HIGH' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
            task.priority === 'MEDIUM' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
            'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
          }`}>{task.priority}</p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("tasks.form.projectLabel")}</label>
          <p className="text-gray-900 dark:text-gray-100">{task.project?.name || t('tasks.form.noProject')}</p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("tasks.form.assigneeLabel")}</label>
          <p className="text-gray-900 dark:text-gray-100">{task.assignee?.firstName || t('tasks.card.unassigned')} {task.assignee?.lastName || ''}</p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("tasks.form.dueDateLabel")}</label>
          <p className="text-gray-900 dark:text-gray-100">{task.dueDate ? format(new Date(task.dueDate), 'PPP p', { locale: dateFnsLocale }) : t('tasks.card.noDueDate')}</p>
        </div>
      </div>
      
      {/* Creation time with relative display */}
      {task.createdAt && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("tasks.form.createdLabel")}</label>
          <p className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {createdAgo || getRelativeTime(task.createdAt, { locale: 'es' })}
            <span className="text-xs text-gray-400">
              ({format(new Date(task.createdAt), 'PPP p', { locale: dateFnsLocale })})
            </span>
          </p>
        </div>
      )}
      
      {task.labels && task.labels.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("tasks.form.labelsLabel")}</label>
          <div className="flex flex-wrap gap-1">
            {task.labels.map((label: string) => (
              <span key={label} className="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                {label}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {task.completedAt && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t("tasks.form.completedAtLabel")}</label>
          <p className="text-green-600 dark:text-green-400">{format(new Date(task.completedAt), 'PPP p', { locale: dateFnsLocale })}</p>
        </div>
      )}
      
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="secondary" onClick={onClose} className="w-full sm:w-auto">
          {t('common.close')}
        </Button>
      </div>
    </div>
  );
};

export const TaskForm: React.FC = () => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateFnsLocale();
  const { taskModalOpen, editingTaskId, isRecurringTask, closeTaskModal, sharedFiles, clearSharedFiles } = useUIStore();
  const { currentTask, createTask, updateTask, fetchTask, isLoading, error: taskError, fetchAttachments, deleteAttachment } = useTaskStore();
  const { projects, fetchProjects } = useProjectStore();
  const { users, fetchUsers } = useUserStore();
  const { user } = useAuthStore();
  const [recurringRule, setRecurringRule] = React.useState<RecurringRule | undefined>();
  const [recurringError, setRecurringError] = React.useState<string | null>(null);
  const [isAutocompleting, setIsAutocompleting] = React.useState(false);
  const [autocompleteDone, setAutocompleteDone] = React.useState(false);
  const [pendingAttachments, setPendingAttachments] = React.useState<PendingAttachment[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<TaskFormData>();

  // FIX: Group C - Check if viewing a completed task (read-only mode)
  const isReadOnly = currentTask?.completed === true;

  // REV-3 FIX: Track selected project for role checking
  const selectedProjectId = watch('projectId');
  
  // REV-5 FIX: Filter users list to show only project members when a project is selected
  const assigneeUsers = useMemo(() => {
    if (!selectedProjectId) return users; // No project: show all org users

    const project = projects.find(p => p.id === selectedProjectId);
    if (!project || !project.members) return []; // Fallback: empty until project/members loaded

    // Build set of member user IDs (including project owner)
    const memberIds = new Set<string>();
    if (project.ownerId) memberIds.add(project.ownerId);
    project.members.forEach((m: { userId: string }) => memberIds.add(m.userId));

    return users.filter(u => memberIds.has(u.id));
  }, [users, selectedProjectId, projects]);

  // REV-3 FIX: Get user's role in the effective project (task's project when editing, dropdown when creating)
  const projectMemberInfo = useMemo(() => {
    const effectiveProjectId = editingTaskId ? currentTask?.projectId : selectedProjectId;
    if (!effectiveProjectId || !user) {
      return { canModify: true, role: null as ProjectRole | null };
    }
    
    const project = projects.find(p => p.id === effectiveProjectId);
    if (!project) {
      return { canModify: true, role: null };
    }
    
    const membership = project.members?.find(m => m.userId === user.id);
    if (!membership) {
      return { canModify: true, role: null };
    }
    
    // ADMIN and MEMBER can modify, VIEWER cannot
    return { 
      canModify: membership.role !== 'VIEWER', 
      role: membership.role 
    };
  }, [editingTaskId, currentTask?.projectId, selectedProjectId, projects, user]);

  // REV-2 FIX: Check if current user is the creator of the task being edited
  const isCreatedByMe = editingTaskId ? currentTask?.creatorId === user?.id : true;

  // BUG FIX: Check user's role in the task's project (not the dropdown) for edit permissions
  const taskProjectId = editingTaskId ? currentTask?.projectId : selectedProjectId;
  const taskProject = projects.find(p => p.id === taskProjectId);
  const taskProjectRole = taskProject?.members?.find(m => m.userId === user?.id)?.role;
  const isTaskProjectAdmin = taskProjectRole === 'ADMIN' || taskProject?.ownerId === user?.id;

  // REV-7 FIX: Proper org-admin check using organizationRoles (mirrors TaskCard.tsx)
  const isOrgAdmin = user?.organizationRoles?.find(r => r.orgId === user?.activeOrganizationId)?.role === 'ADMIN';

  useEffect(() => {
    fetchProjects();
    fetchUsers();
  }, [fetchProjects, fetchUsers]);

  // Separate effect to fetch task when editingTaskId changes
  useEffect(() => {
    if (editingTaskId) {
      fetchTask(editingTaskId);
    }
  }, [editingTaskId, fetchTask]);

  // Fetch existing attachments when editing a task
  useEffect(() => {
    if (editingTaskId) {
      fetchAttachments(editingTaskId);
    }
  }, [editingTaskId, fetchAttachments]);

  // REV-5 FIX: Reset assigneeId when project changes if current assignee is not a member of new project
  useEffect(() => {
    if (!selectedProjectId) return;
    const currentAssigneeId = watch('assigneeId');
    if (currentAssigneeId && !assigneeUsers.some(u => u.id === currentAssigneeId)) {
      setValue('assigneeId', '', { shouldDirty: true });
    }
  }, [selectedProjectId, assigneeUsers, setValue, watch]);

  // Clear form function - called when user clicks X or Cancel (not backdrop/escape)
  const clearForm = () => {
    reset({
      title: '',
      description: '',
      priority: 'MEDIUM',
      dueDate: '',
      remindAt: '',
      projectId: '',
      assigneeId: '',
      labels: '',
      isPrivate: false,
      isGroupTask: false,
    });
    setRecurringRule(undefined);
    setRecurringError(null);
    setPendingAttachments([]);
  };

  // Effect to populate form when editing a task
  useEffect(() => {
    if (currentTask) {
      reset({
        title: currentTask.title,
        description: currentTask.description || '',
        priority: currentTask.priority,
        dueDate: currentTask.dueDate ? format(new Date(currentTask.dueDate), "yyyy-MM-dd'T'HH:mm", { locale: dateFnsLocale }) : '',
        remindAt: currentTask.remindAt ? format(new Date(currentTask.remindAt), "yyyy-MM-dd'T'HH:mm", { locale: dateFnsLocale }) : '',
        projectId: currentTask.projectId || '',
        assigneeId: currentTask.assigneeId || '',
        labels: currentTask.labels?.join(', ') || '',
        isPrivate: currentTask.isPrivate,
        isGroupTask: currentTask.isGroupTask,
      });
      setRecurringRule(currentTask.recurringRule);
    } else if (!editingTaskId) {
      // New task (no currentTask, no editingTaskId) - reset to defaults
      reset({
        title: '',
        description: '',
        priority: 'MEDIUM',
        dueDate: '',
        remindAt: '',
        projectId: '',
        assigneeId: '',
        labels: '',
        isPrivate: false,
        isGroupTask: false,
      });
      // Initialize recurring rule automatically when creating a recurring task
      if (isRecurringTask) {
        setRecurringRule({
          frequency: 'DAILY',
          interval: 1,
        });
      } else {
        setRecurringRule(undefined);
      }
    }
    // Clear error when modal opens
    setRecurringError(null);
  }, [currentTask, editingTaskId, isRecurringTask, reset]);

  // FIX: Sync isGroupTask based on business rules when projectId or assigneeId change
  // - If assigneeId has value (individual assignee), force isGroupTask = false
  // - If no projectId (personal task without project), force isGroupTask = false
  // - If projectId exists AND no assigneeId (Unassigned within a project), default isGroupTask = true (pre-select Group)
  useEffect(() => {
    // ISSUE 2 FIX: Also sync in edit mode — when assignee is selected, reset isGroupTask to false
    const currentAssigneeId = watch('assigneeId');
    const currentProjectId = watch('projectId');

    if (currentAssigneeId) {
      // Individual assignee selected → must not be group task
      // shouldDirty: true ensures the field is marked as dirty so the payload includes isGroupTask: false
      setValue('isGroupTask', false, { shouldDirty: true });
    } else if (!editingTaskId && !currentProjectId) {
      // No project in new task → personal task, cannot be group task
      setValue('isGroupTask', false, { shouldDirty: true });
    } else if (!editingTaskId) {
      // Project selected and Unassigned in new task → default to Group task (pre-select)
      setValue('isGroupTask', true, { shouldDirty: true });
    }
    // In edit mode with no assignee: preserve existing isGroupTask value (don't force change)
  }, [watch('projectId'), watch('assigneeId'), editingTaskId, setValue, watch]);

  const onSubmit = async (data: TaskFormData) => {
    // Clear any previous error
    setRecurringError(null);

    const attachmentIds = [
      ...(currentTask?.attachments?.map((a) => a.id) ?? []),
      ...pendingAttachments.filter((a) => !a.isUploading).map((a) => a.id),
    ];

    const taskData: CreateTaskRequest | UpdateTaskRequest = {
      title: data.title,
      description: data.description || undefined,
      priority: data.priority,
      dueDate: data.dueDate || undefined,
      remindAt: data.remindAt || undefined,
      projectId: data.projectId || null,
      assigneeId: data.assigneeId || null,
      labels: data.labels ? data.labels.split(',').map(l => l.trim()).filter(Boolean) : undefined,
      isPrivate: editingTaskId ? (dirtyFields.isPrivate ? data.isPrivate : undefined) : data.isPrivate,
      isGroupTask: editingTaskId ? (dirtyFields.isGroupTask ? data.isGroupTask : undefined) : data.isGroupTask,
      recurringRule: recurringRule,
      attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
    };

    if (editingTaskId) {
      const success = await updateTask(editingTaskId, taskData as UpdateTaskRequest);
      if (success) {
        if (recurringRule) {
          window.dispatchEvent(new CustomEvent('recurringTaskUpdated'));
        }
        closeTaskModal();
      }
    } else {
      const newTask = await createTask(taskData as CreateTaskRequest);
      if (newTask) {
        if (recurringRule) {
          window.dispatchEvent(new CustomEvent('recurringTaskCreated'));
        }
        clearForm();
        closeTaskModal();
      }
    }
  };

  // AI Autocomplete handler - only available when creating a new task
  const handleAutocomplete = async () => {
    const description = watch('description');
    if (!description || !description.trim()) return;

    const isShortDescription = description.trim().length < 10;

    setIsAutocompleting(true);
    setAutocompleteDone(false);
    try {
      const response = await aiChatApi.autocomplete(description);
      if (response.success && response.data) {
        const suggestions = response.data;

        // Check if we got an empty or trivially-empty data object
        const hasSuggestions = suggestions && typeof suggestions === 'object' && Object.keys(suggestions).length > 0;
        if (!hasSuggestions) {
          // Show feedback for all descriptions; short descriptions get an informative hint
          if (isShortDescription) {
            showToast(t('tasks.form.autocompleteLongerDescription'), 'info');
          } else {
            showToast(t('tasks.form.autocompleteNoSuggestions'), 'info');
          }
          return;
        }

        // Batch all suggested field values into a single reset() call
        // to prevent sequential resets from overwriting each other
        const currentValues = watch();
        const updates: Partial<TaskFormData> = {};

        // Only fill title if currently empty
        if (suggestions.title && !currentValues.title) {
          updates.title = suggestions.title;
        }
        // Always apply the cleaned/corrected description (main purpose of autofill)
        if (suggestions.description && typeof suggestions.description === 'string') {
          updates.description = suggestions.description;
        }
        // Only fill priority if currently at default (MEDIUM) or not set
        if (suggestions.priority && (!currentValues.priority || currentValues.priority === 'MEDIUM')) {
          updates.priority = suggestions.priority as TaskPriority;
        }
        // Only fill date/assignment fields if currently empty
        if (suggestions.dueDate && !currentValues.dueDate) {
          const d = new Date(suggestions.dueDate);
          updates.dueDate = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}T${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
        }
        if (suggestions.remindAt && !currentValues.remindAt) {
          const d = new Date(suggestions.remindAt);
          updates.remindAt = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}T${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
        }
        if (suggestions.projectId && !currentValues.projectId) {
          updates.projectId = suggestions.projectId;
        }
        if (suggestions.assigneeId && !currentValues.assigneeId) {
          updates.assigneeId = suggestions.assigneeId;
        }
        // Handle labels: AI returns string[] but form expects comma-separated string
        if (suggestions.labels && !currentValues.labels) {
          if (Array.isArray(suggestions.labels)) {
            updates.labels = suggestions.labels.join(', ');
          } else if (typeof suggestions.labels === 'string') {
            updates.labels = suggestions.labels;
          }
        }
        // Only set isPrivate if explicitly suggested (and not already set by user)
        if (suggestions.isPrivate !== undefined && !currentValues.isPrivate) {
          updates.isPrivate = suggestions.isPrivate;
        }

        // Apply all updates in a single reset call
        if (Object.keys(updates).length > 0) {
          reset({ ...currentValues, ...updates });
        }

        setAutocompleteDone(true);
        setTimeout(() => setAutocompleteDone(false), 2000);
      } else {
        // API returned unsuccessful response
        if (isShortDescription) {
          showToast(t('tasks.form.autocompleteLongerDescription'), 'info');
        } else {
          showToast(t('tasks.form.autocompleteError'), 'error');
        }
      }
    } catch (error) {
      console.error('Autocomplete failed:', error);
      // Show feedback for all descriptions; short descriptions get an informative hint
      if (isShortDescription) {
        showToast(t('tasks.form.autocompleteLongerDescription'), 'info');
      } else {
        showToast(t('tasks.form.autocompleteUnavailable'), 'error');
      }
    } finally {
      setIsAutocompleting(false);
    }
  };

  return (
    <Modal
      isOpen={taskModalOpen}
      onClose={closeTaskModal}
      onClear={clearForm}
      title={isReadOnly ? t('tasks.form.viewTaskCompleted') : (editingTaskId ? t('tasks.form.saveChanges') : t('tasks.form.newTask'))}
      size="lg"
      hasUnsavedChanges={isDirty && !isReadOnly}
    >
      {/* FIX: Group C - Show read-only banner for completed tasks */}
      {isReadOnly && (
        <div className="mb-4 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 flex items-center gap-2">
          <Eye className="w-5 h-5 text-amber-500" />
          <span className="text-sm text-amber-700 dark:text-amber-300">
            {t("tasks.form.readOnlyBanner")}
          </span>
        </div>
      )}
      
      {/* Show recurring-related errors */}
      {recurringError && (
        <div className="mb-4 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <span className="text-sm text-amber-700 dark:text-amber-300">{recurringError}</span>
        </div>
      )}

      {/* Show task error (permission denied, validation errors, etc.) */}
      {taskError && (
        <div className="mb-4 p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{taskError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* FIX: Group C - In read-only mode, show fields as static text */}
        {isReadOnly && currentTask ? (
          <TaskViewMode task={currentTask} currentUserId={user?.id} onClose={closeTaskModal} />
        ) : (
          <>
            <Input
              label={t("tasks.form.titleLabel")}
              placeholder={t('tasks.form.titlePlaceholder')}
              error={errors.title?.message}
              {...register('title', { required: t('tasks.form.titleRequired') })}
            />

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("tasks.form.descriptionLabel")}
                </label>
                {!editingTaskId && (
                  <button
                    type="button"
                    onClick={handleAutocomplete}
                    disabled={isAutocompleting || !watch('description')?.trim()}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title={t("tasks.form.autofillTitle")}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {isAutocompleting ? t('tasks.form.filling') : autocompleteDone ? t('tasks.form.done') : t('tasks.form.autofill')}
                  </button>
                )}
              </div>
              <textarea
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:dark:bg-gray-700 disabled:cursor-not-allowed"
                rows={3}
                placeholder={t("tasks.form.descriptionPlaceholder")}
                {...register('description')}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t("tasks.form.priorityLabel")}
                </label>
                <select
                  className="w-full px-3 py-3 sm:py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px] disabled:bg-gray-100 disabled:dark:bg-gray-700 disabled:cursor-not-allowed"
                  {...register('priority')}
                >
                  <option value="LOW">{t("tasks.card.priority.low")}</option>
                  <option value="MEDIUM">{t("tasks.card.priority.medium")}</option>
                  <option value="HIGH">{t("tasks.card.priority.high")}</option>
                  <option value="URGENT">{t("tasks.card.priority.urgent")}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t("tasks.form.projectLabel")}
                </label>
                <select
                  className="w-full px-3 py-3 sm:py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px] disabled:bg-gray-100 disabled:dark:bg-gray-700 disabled:cursor-not-allowed"
                  {...register('projectId')}
                >
                  <option value="">{t('tasks.form.noProject')}</option>
                  {(() => {
                    // Detect duplicate project names and compute display labels
                    const nameCount: Record<string, number> = {};
                    projects.forEach(p => {
                      nameCount[p.name] = (nameCount[p.name] || 0) + 1;
                    });
                    return projects.map((project) => {
                      const isDuplicate = nameCount[project.name] > 1;
                      const displayName = isDuplicate && project.owner
                        ? project.name + " (" + t("tasks.form.createdBy", { name: project.owner.firstName }) + ")"
                        : project.name;
                      return (
                        <option key={project.id} value={project.id}>
                          {displayName}
                        </option>
                      );
                    });
                  })()}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t("tasks.form.assignToLabel")}
                </label>
                <select
                  className="w-full px-3 py-3 sm:py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px] disabled:bg-gray-100 disabled:dark:bg-gray-700 disabled:cursor-not-allowed"
                  {...register('assigneeId')}
                >
                  <option value="">{t('tasks.form.unassigned')}</option>
                  {assigneeUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Task Type Selector - shows when project is selected but no assignee */}
            {watch('projectId') && !watch('assigneeId') && (
              <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t("tasks.form.taskTypeForProjectMembers")}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className={`
                    flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                    ${watch('isGroupTask') 
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30' 
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'}
                  `}>
                    <input
                      type="radio"
                      className="mt-0.5"
                      checked={watch('isGroupTask')}
                      onChange={() => {}}
                      onClick={() => reset({ ...watch(), isGroupTask: true })}
                    />
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                        <Users className="w-4 h-4" />
                        {t("tasks.form.groupTask")}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t("tasks.form.groupTaskDescription")}
                      </p>
                    </div>
                  </label>

                  <label className={`
                    flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                    ${!watch('isGroupTask') 
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30' 
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'}
                  `}>
                    <input
                      type="radio"
                      className="mt-0.5"
                      checked={!watch('isGroupTask')}
                      onChange={() => {}}
                      onClick={() => reset({ ...watch(), isGroupTask: false })}
                    />
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                        <User className="w-4 h-4" />
                        {t("tasks.form.individualTask")}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t("tasks.form.individualTaskDescription")}
                      </p>
                    </div>
                  </label>
                </div>

                {/* Notification info for project tasks */}
                <div className="mt-3 p-2 rounded bg-blue-100 dark:bg-blue-900/30 text-xs text-blue-700 dark:text-blue-300">
                  <strong>{t("tasks.form.note")}</strong> {t("tasks.form.allMembersNotified")}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('tasks.form.dueDate')}
                type="datetime-local"
                {...register('dueDate')}
              />

              <Input
                label={t('tasks.form.remindMe')}
                type="datetime-local"
                {...register('remindAt')}
              />
            </div>

            <Input
              label={t("tasks.form.labelsLabel")}
              placeholder={t('tasks.form.labelsPlaceholder')}
              hint={t('tasks.form.labelsHint')}
              {...register('labels')}
            />

            {/* Recurring Task Configuration */}
            {!editingTaskId && (
              <RecurringRuleForm
                value={recurringRule}
                onChange={setRecurringRule}
                required={isRecurringTask}
              />
            )}

            {/* Edit mode - show pause controls for recurring tasks */}
            {editingTaskId && currentTask?.recurringRule && (
              <RecurringRuleForm
                value={recurringRule}
                onChange={setRecurringRule}
                showPauseControls={true}
                onPause={async () => {
                  // Call API to pause
                  try {
                    const response = await fetch(`/api/tasks/recurring/${editingTaskId}/pause`, {
                      method: 'PATCH',
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                      },
                    });
                    if (response.ok) {
                      setRecurringRule(prev => prev ? { ...prev, isPaused: true } : undefined);
                    }
                  } catch (error) {
                    console.error('Failed to pause recurring task:', error);
                  }
                }}
                onResume={async () => {
                  // Call API to resume
                  try {
                    const response = await fetch(`/api/tasks/recurring/${editingTaskId}/resume`, {
                      method: 'PATCH',
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                      },
                    });
                    if (response.ok) {
                      setRecurringRule(prev => prev ? { ...prev, isPaused: false } : undefined);
                    }
                  } catch (error) {
                    console.error('Failed to resume recurring task:', error);
                  }
                }}
              />
            )}

            {/* Privacy Toggle */}
            <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <input
                type="checkbox"
                id="isPrivate"
                {...register('isPrivate')}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div className="flex-1">
                <label htmlFor="isPrivate" className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
                  {((watch && watch('isPrivate')) || false) ? (
                    <Lock className="w-4 h-4 text-amber-500" />
                  ) : (
                    <Globe className="w-4 h-4 text-gray-400" />
                  )}
                  {t("tasks.form.privateTask")}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t("tasks.form.privateTaskDescription")}
                </p>
              </div>
            </div>

            {/* REV-3 FIX: Role restriction warning for VIEWERs */}
            {selectedProjectId && !projectMemberInfo.canModify && (
              <div className="flex items-start gap-3 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    {t("tasks.form.viewOnlyAccess")}
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                    {t("tasks.form.viewerRoleDescription")}
                  </p>
                </div>
              </div>
            )}

            {/* Image attachments */}
            {!isReadOnly && (
              <TaskAttachmentSection
                existingAttachments={currentTask?.attachments ?? []}
                pendingAttachments={pendingAttachments}
                onPendingChange={setPendingAttachments}
                onExistingRemove={async (id) => {
                  if (editingTaskId) {
                    await deleteAttachment(id, editingTaskId);
                  }
                }}
                initialFiles={sharedFiles.length > 0 ? sharedFiles : undefined}
                onInitialFilesConsumed={clearSharedFiles}
              />
            )}

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="button" variant="secondary" onClick={() => { clearForm(); closeTaskModal(); }} className="w-full sm:w-auto min-h-[48px] sm:min-h-0">
                {t('common.cancel')}
              </Button>
              {/* Show Create for all, show Save Changes for creator/org-admin/project-admin (disabled for VIEWERs in projects) */}
              {(!editingTaskId || isCreatedByMe || isOrgAdmin || isTaskProjectAdmin) && (
                <Button 
                  type="submit" 
                  isLoading={isLoading}
                  disabled={!!(selectedProjectId && !projectMemberInfo.canModify)}
                  className="w-full sm:w-auto min-h-[48px] sm:min-h-0"
                >
                  {editingTaskId ? t('tasks.form.saveChanges') : t('tasks.form.createTask')}
                </Button>
              )}
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};

export default TaskForm;
